import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DocumentoSocial } from "@/hooks/useDocumentosSociales";
import { FileText, Download, Trash2, Calendar, Eye } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface DocumentosListProps {
  documentos: DocumentoSocial[];
  onDelete: (documento: DocumentoSocial) => Promise<boolean>;
  loading?: boolean;
}

export const DocumentosList = ({ documentos, onDelete, loading }: DocumentosListProps) => {
  const [documentoAEliminar, setDocumentoAEliminar] = useState<DocumentoSocial | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!documentoAEliminar) return;
    
    setDeleting(true);
    const success = await onDelete(documentoAEliminar);
    setDeleting(false);
    
    if (success) {
      setDocumentoAEliminar(null);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "Desconocido";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2 mt-2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (documentos.length === 0) {
    return (
      <Card className="text-center py-12">
        <CardContent>
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No hay documentos disponibles</p>
          <p className="text-sm text-muted-foreground mt-2">
            Comienza subiendo tu primer documento
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documentos.map((documento) => (
          <Card key={documento.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <FileText className="w-8 h-8 text-viable-orange" />
                <Badge variant="outline" className="text-xs">
                  {formatFileSize(documento.tamano_archivo)}
                </Badge>
              </div>
              <CardTitle className="text-base line-clamp-2 mt-2">
                {documento.nombre_documento}
              </CardTitle>
              {documento.descripcion && (
                <CardDescription className="line-clamp-2">
                  {documento.descripcion}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {documento.fecha_documento && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4 mr-2" />
                  {format(new Date(documento.fecha_documento), "dd 'de' MMMM, yyyy", { locale: es })}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => window.open(documento.url_archivo, '_blank')}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Ver
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = documento.url_archivo;
                    link.download = documento.nombre_documento;
                    link.click();
                  }}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Descargar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDocumentoAEliminar(documento)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={!!documentoAEliminar} onOpenChange={() => setDocumentoAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El documento "{documentoAEliminar?.nombre_documento}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
